import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ProdutoTabelaEspecialItens } from '../../core/models/produtos/produto-tabela-especial-itens';

@Component({
    selector: 'app-tabela-produtos-editavel',
    templateUrl: './tabela-produtos-editavel.component.html',
    styleUrls: ['./tabela-produtos-editavel.component.css'],
    standalone: false
})
export class TabelaProdutosEditavelComponent implements OnInit {
  @Input() produtos: ProdutoTabelaEspecialItens[] = [];
  @Input() exibirLote: boolean = false;
  private _filtroAtivo = 'todos';
  @Input() set filtroAtivo(valor: string) {
    if (valor === this._filtroAtivo) return;
    if (this.editandoCelula) this.cancelarEdicao(this.editandoCelula.index);
    this._filtroAtivo = valor;
    this.produtosSelecionados.clear();
    this.todosSelecionados = false;
  }

  produtoVisivel(produto: ProdutoTabelaEspecialItens): boolean {
    return this._filtroAtivo === 'todos' || !!produto.ativo === (this._filtroAtivo === 'ativo');
  }

  get indicesVisiveis(): number[] {
    return this.produtos.reduce((indices, produto, index) => {
      if (this.produtoVisivel(produto) || this.editandoCelula?.index === index) indices.push(index);
      return indices;
    }, [] as number[]);
  }
  @Output() editarItem = new EventEmitter<{codigo: string, qtd_minima: number}>();
  @Output() verLogItem = new EventEmitter<ProdutoTabelaEspecialItens>();

  /* Abre o log de uso da cota deste item (não dispara a edição inline da célula). */
  abrirLogItem(event: Event, produto: ProdutoTabelaEspecialItens) {
    event.stopPropagation();
    this.verLogItem.emit(produto);
  }

  /* Edição Inline */
  editandoCelula: { index: number; campo: string } = null;
  valorOriginal: any = null;

  /* Edição em Massa */
  produtosSelecionados: Set<number> = new Set();
  todosSelecionados: boolean = false;
  valorOriginalMassa: any[] = [];

  edicaoEmMassa = {
    percentual: null,
    tipo_calculo: null,
    preco: null,
    qtd_minima: null,
    ativo: null
  };

  /* Console para Debug */
  console = console;

  constructor(private toastr: ToastrService) { }

  ngOnInit(): void {
  }

  /* ===== EDIÇÃO INLINE ===== */

  iniciarEdicao(index: number, campo: string) {
    this.editandoCelula = { index, campo };
    this.valorOriginal = this.produtos[index][campo];
  }

  finalizarEdicao(index: number, campo: string) {
    if (this.editandoCelula && this.editandoCelula.index === index && this.editandoCelula.campo === campo) {
      // Recalcular antes de validar para ter os dois campos atualizados
      if (campo === 'percentual' || campo === 'tipo_calculo') {
        this.recalcularPreco(index);
      }
      if (campo === 'preco') {
        this.recalcularPercentual(index);
      }

      // Validar consistência
      const erro = this.validarValores(index);
      if (erro) {
        this.toastr.error(erro, 'Valor inválido');
        // Reverter o campo editado para o valor original
        this.produtos[index][campo] = this.valorOriginal;
        // Recalcular novamente com o valor revertido para manter consistência
        if (campo === 'percentual' || campo === 'tipo_calculo') {
          this.recalcularPreco(index);
        }
        if (campo === 'preco') {
          this.recalcularPercentual(index);
        }
        this.editandoCelula = null;
        this.valorOriginal = null;
        return;
      }

      this.toastr.success('Alteração salva!', 'Sucesso');
      this.editandoCelula = null;
      this.valorOriginal = null;
      this.atualizarCheckboxTodosSelecionados();
    }
  }

  cancelarEdicao(index: number) {
    if (this.editandoCelula && this.editandoCelula.index === index) {
      this.produtos[index][this.editandoCelula.campo] = this.valorOriginal;
      this.editandoCelula = null;
      this.valorOriginal = null;
    }
  }

  /* ===== RECALCULAR PREÇO ===== */

  recalcularPreco(index: number) {
    const produto = this.produtos[index];
    if (produto.price == null || produto.percentual == null || produto.tipo_calculo == null) {
      return;
    }

    const priceBase = produto.price;
    const percentual = produto.percentual;

    if (produto.tipo_calculo === 'margem') {
      produto.preco = priceBase * (1 + percentual / 100);
    } else if (produto.tipo_calculo === 'desconto') {
      produto.preco = priceBase * (1 - percentual / 100);
    }
  }

  recalcularPercentual(index: number) {
    const produto = this.produtos[index];

    if (produto == null || produto.price == null || produto.preco == null || produto.tipo_calculo == null) {
      return;
    }

    const priceBase = Number(produto.price);
    const preco = Number(produto.preco);

    if (!isFinite(priceBase) || !isFinite(preco) || priceBase <= 0) {
      return;
    }

    let percentual: number;

    if (produto.tipo_calculo === 'margem') {
      // preco = priceBase * (1 + p/100)  => p = ((preco/priceBase) - 1) * 100
      percentual = ((preco / priceBase) - 1) * 100;
    } else if (produto.tipo_calculo === 'desconto') {
      // preco = priceBase * (1 - p/100)  => p = (1 - (preco/priceBase)) * 100
      percentual = (1 - (preco / priceBase)) * 100;
    } else {
      return;
    }

    percentual = Math.round(percentual * 10000) / 10000;
    if (Object.is(percentual, -0)) { percentual = 0; }

    produto.percentual = percentual;
  }

  /* ===== EDIÇÃO EM MASSA ===== */

  selecionarProduto(index: number) {
    const id = this.produtos[index].id;
    if (this.produtosSelecionados.has(id)) {
      this.produtosSelecionados.delete(id);
    } else {
      this.produtosSelecionados.add(id);
    }
    this.atualizarCheckboxTodosSelecionados();
  }

  estaProdutoSelecionado(index: number): boolean {
    return this.produtosSelecionados.has(this.produtos[index].id);
  }

  selecionarTodos() {
    if (this.todosSelecionados) {
      this.produtosSelecionados.clear();
      this.todosSelecionados = false;
    } else {
      this.produtosSelecionados.clear();
      for (let produto of this.produtos) {
        if (this.produtoVisivel(produto)) this.produtosSelecionados.add(produto.id);
      }
      this.atualizarCheckboxTodosSelecionados();
    }
  }

  atualizarCheckboxTodosSelecionados() {
    const visiveis = this.produtos.filter(produto => this.produtoVisivel(produto));
    const idsVisiveis = new Set(visiveis.map(produto => produto.id));
    for (const id of this.produtosSelecionados) {
      if (!idsVisiveis.has(id)) this.produtosSelecionados.delete(id);
    }
    this.todosSelecionados = visiveis.length > 0 && visiveis.every(produto => this.produtosSelecionados.has(produto.id));
  }

  aplicarEdicaoEmMassa() {
    if (this.produtosSelecionados.size === 0) {
      this.toastr.info("Selecione pelo menos um produto!", "Informação");
      return;
    }

    let alteracoes = 0;

    // Captura snapshot dos valores antes de alterar (para revert em caso de erro)
    const valorOriginalMassa: any[] = this.produtos.map(p => ({
      percentual: p.percentual,
      tipo_calculo: p.tipo_calculo,
      preco: p.preco
    }));
    this.valorOriginalMassa = valorOriginalMassa;

    for (let i = 0; i < this.produtos.length; i++) {
      if (this.produtosSelecionados.has(this.produtos[i].id) && this.produtoVisivel(this.produtos[i])) {
        if (this.edicaoEmMassa.percentual != null) {
          this.produtos[i].percentual = this.edicaoEmMassa.percentual;
          alteracoes++;
        }
        if (this.edicaoEmMassa.tipo_calculo != null) {
          this.produtos[i].tipo_calculo = this.edicaoEmMassa.tipo_calculo;
          alteracoes++;
        }
        if (this.edicaoEmMassa.preco != null) {
          this.produtos[i].preco = this.edicaoEmMassa.preco;
          alteracoes++;
        }
        if (this.edicaoEmMassa.qtd_minima != null) {
          this.produtos[i].qtd_minima = this.edicaoEmMassa.qtd_minima;
          alteracoes++;
        }
        if (this.edicaoEmMassa.ativo != null) {
          this.produtos[i].ativo = this.edicaoEmMassa.ativo;
          alteracoes++;
        }

        // Recalcular: percentual -> preco (padrão) ou preco -> percentual
        if (this.edicaoEmMassa.preco != null) {
          // preco foi definido explicitamente: recalcular percentual para manter consistência
          this.recalcularPercentual(i);
        } else if (this.edicaoEmMassa.percentual != null || this.edicaoEmMassa.tipo_calculo != null) {
          this.recalcularPreco(i);
        }

        // Validar: se inválido, reverter e não contar como alteração
        const erro = this.validarValores(i);
        if (erro) {
          this.toastr.error(`${this.produtos[i].name}: ${erro}`, 'Valor inválido');
          // Reverter campos alterados neste item
          if (this.edicaoEmMassa.percentual != null) { this.produtos[i].percentual = this.valorOriginalMassa[i].percentual; }
          if (this.edicaoEmMassa.tipo_calculo != null) { this.produtos[i].tipo_calculo = this.valorOriginalMassa[i].tipo_calculo; }
          if (this.edicaoEmMassa.preco != null) { this.produtos[i].preco = this.valorOriginalMassa[i].preco; }
          // Recalcular com valores revertidos
          this.recalcularPreco(i);
          alteracoes--; // desfaz a contagem
          continue;
        }
      }
    }

    this.toastr.success(`${this.produtosSelecionados.size} produto(s) alterado(s) e salvos!`, "Sucesso");
    this.limparEdicaoEmMassa();
    this.atualizarCheckboxTodosSelecionados();
  }

  limparEdicaoEmMassa() {
    this.edicaoEmMassa = {
      percentual: null,
      tipo_calculo: null,
      preco: null,
      qtd_minima: null,
      ativo: null
    };
  }



  /* ===== AUXILIAR ===== */

  /**
   * Valida consistência entre preço, percentual e tipo_calculo.
   * Retorna null se ok, ou uma string com a mensagem de erro.
   */
  private validarValores(index: number): string | null {
    const p = this.produtos[index];
    if (p.price == null || p.price <= 0) { return null; } // sem base, não valida
    if (p.tipo_calculo == null) { return null; }

    const base = Number(p.price);

    // Validar pelo percentual
    if (p.percentual != null) {
      const perc = Number(p.percentual);
      if (!isFinite(perc)) { return 'Percentual inválido.'; }

      if (p.tipo_calculo === 'desconto') {
        if (perc < 0) { return 'Desconto não pode ser negativo.'; }
        if (perc >= 100) { return 'Desconto não pode ser 100% ou mais (preço seria zero ou negativo).'; }
      }

      if (p.tipo_calculo === 'margem') {
        if (perc < 0) { return 'Margem não pode ser negativa.'; }
      }
    }

    // Validar pelo preço digitado
    if (p.preco != null) {
      const preco = Number(p.preco);
      if (!isFinite(preco) || preco <= 0) { return 'Preço deve ser maior que zero.'; }

      if (p.tipo_calculo === 'desconto') {
        if (preco >= base) {
          return `Preço (R$ ${preco.toFixed(2)}) não pode ser maior ou igual ao preço base (R$ ${base.toFixed(2)}) no modo Desconto.`;
        }
      }

      if (p.tipo_calculo === 'margem') {
        if (preco <= 0) { return 'Preço deve ser maior que zero no modo Margem.'; }
      }
    }

    return null;
  }

  estaEditando(index: number, campo: string): boolean {
    return this.editandoCelula && this.editandoCelula.index === index && this.editandoCelula.campo === campo;
  }
}
